// Sekce Objednávky – seznam se sdíleným toolbar/kartami z shared/list-ui.js.
// Stránkovaný list endpoint (limit/offset) vrací ořezané záznamy vč. customerName.
// Detail objednávky = editor po vzoru nabídek: A4 náhled, poznámka, PDF, tisk.
import logoUrl from 'url:../images/logo-icon.svg';
import { icon } from './icons.js';
import { buildFinishingLines, stripFinishingFromName } from './shared/offer-item-format.js';
import {
  API_BASE_URL, ListView, renderListCard, renderErrorState,
  escapeHtml, formatDateTime, formatMoney, customerNameOf, customerDataText,
  statusBadge, statusLabel, STATUS_META
} from './shared/list-ui.js';

const ORDER_STATUS_ORDER = ['received', 'in_progress', 'done', 'to_invoice', 'invoiced', 'delivered', 'cancelled'];

export class OrdersListManager {
  constructor() {
    this.orders = [];
    this.view = new ListView({
      key: 'orders',
      title: 'Objednávky',
      subtitle: 'Přehled zakázek a jejich stav.',
      searchPlaceholder: 'Hledat (číslo, firma, položka)…',
      statusKind: 'order',
      statuses: ORDER_STATUS_ORDER,
      authorLabels: { me: 'Moje objednávky' },
      enableCustomerFilter: true,
      enableKanban: true,
      extraToolbar: () => this.renderExportDropdown(),
      onChange: () => this.load()
    });
  }

  // ---------- Seznam ----------

  async showSection() {
    await this.load();
  }

  isDetailView() {
    return Boolean(document.getElementById('orders-back-btn'));
  }

  /** Tichý reload (auto-refresh) – nesahá na otevřený detail, neukazuje spinner. */
  refresh() {
    if (this.isDetailView()) return;
    this.load({ silent: true });
  }

  async load({ silent = false } = {}) {
    const container = document.getElementById('objednavky-section-content');
    if (!container) return;
    if (!document.getElementById('orders-list')) {
      this.view.renderShell(container);
      this._bindListEvents();
      this._bindExportEvents();
    } else if (!silent) {
      this.view.renderLoading();
    }

    try {
      if (this.view.viewMode === 'kanban') {
        await this.loadKanban();
        return;
      }
      const params = this.view.buildParams();
      const response = await fetch(`${API_BASE_URL}/orders?${params}`, { credentials: 'include' });
      if (!response.ok) throw new Error('Chyba při načítání objednávek');
      const result = await response.json();
      this.orders = result.data || [];
      if (Array.isArray(result.statuses) && result.statuses.length) this.view.statuses = result.statuses;
      if (Array.isArray(result.authors)) this.view.authors = result.authors;

      this.view.renderResults({
        rowsHtml: this.orders.map((o) => this.renderOrderRow(o)),
        total: result.total,
        emptyState: {
          title: 'Zatím nemáte žádné objednávky',
          hint: 'Objednávka vznikne převedením schválené nabídky tlačítkem „Převést na objednávku“ v editoru nabídky.'
        }
      });
    } catch (error) {
      console.error('Chyba při načítání objednávek:', error);
      this.view.renderCustom(renderErrorState('Nepodařilo se načíst objednávky', 'window.ordersListManager.load()'));
    }
  }

  async loadKanban() {
    const { renderKanbanBoard } = await import('./shared/kanban.js');
    await renderKanbanBoard({
      view: this.view,
      statusKind: 'order',
      fetchColumn: async (status) => {
        const params = this.view.buildParams({ paginate: false });
        params.set('status', status);
        params.set('limit', '30');
        params.set('sort', 'kanban');
        const res = await fetch(`${API_BASE_URL}/orders?${params}`, { credentials: 'include' });
        if (!res.ok) throw new Error('Chyba při načítání objednávek');
        const result = await res.json();
        return { items: result.data || [], total: result.total || 0 };
      },
      cardData: (o) => ({
        id: o.id,
        title: `#${o.id}`,
        customerName: customerNameOf(o),
        meta: formatDateTime(o.createdAt),
        price: formatMoney(o.totalPrice),
        orderKey: typeof o.kanbanOrder === 'number' ? o.kanbanOrder : Date.parse(o.createdAt)
      }),
      onOpen: (id) => this.openDetail(id),
      onDrop: async (id, { status, statusChanged, orderKey }) => {
        if (statusChanged) {
          await this.setStatus(id, status, 'Změna stavu přetažením v kanbanu');
        }
        await this.saveKanbanOrder(id, orderKey);
      }
    });
  }

  renderOrderRow(o) {
    const itemsCount = o.itemsCount != null ? o.itemsCount : (o.items || []).length;
    const itemsText = itemsCount === 1 ? '1 položka' : `${itemsCount} položek`;
    const itemNames = (o.items || []).map((i) => escapeHtml(i.name)).join(', ');
    const badges = [];
    if (o.invoicedAsId) {
      badges.push(`
        <span class="inline-flex items-center gap-0.5 text-xs text-indigo-700">
          ${icon('receipt', 'w-3 h-3')}
          <button type="button" class="underline hover:no-underline" data-action="open-invoice" data-id="${escapeHtml(o.invoicedAsId)}" title="Otevřít fakturu ${escapeHtml(o.invoicedAsId)}">${escapeHtml(o.invoicedAsId)}</button>
        </span>
      `);
    }

    return renderListCard({
      id: o.id,
      title: `Objednávka <span class="font-semibold">#${escapeHtml(o.id)}</span>`,
      status: { kind: 'order', value: o.status || 'received' },
      customerName: customerNameOf(o),
      metaLine: `
        <span class="inline-flex items-center">${icon('calendar', 'w-3.5 h-3.5 mr-1 text-gray-400')}${formatDateTime(o.createdAt)}</span>
        <span>·</span><span>${itemsText}</span>
        ${o.createdByName ? `<span>·</span><span>${escapeHtml(o.createdByName)}</span>` : ''}
        ${o.offerId ? `<span>·</span><span>z nabídky #${escapeHtml(o.offerId)}</span>` : ''}
      `,
      description: itemNames,
      price: formatMoney(o.totalPrice),
      priceNote: 'bez DPH',
      badges,
      compact: this.view.compact,
      actions: [
        { action: 'open', icon: 'eye', label: 'Detail objednávky', tone: 'primary' },
        { action: 'pdf', icon: 'document-arrow-down', label: 'Stáhnout PDF objednávky' },
        { action: 'delete', icon: 'trash', label: 'Smazat objednávku', tone: 'danger' }
      ]
    });
  }

  _bindListEvents() {
    const list = this.view.listContainer;
    if (!list) return;
    list.addEventListener('click', (e) => {
      const actionBtn = e.target.closest('[data-action]');
      if (actionBtn) {
        e.stopPropagation();
        const { action, id } = actionBtn.dataset;
        if (action === 'open') this.openDetail(id);
        else if (action === 'pdf') this.downloadPdf(id);
        else if (action === 'delete') this.confirmDelete(id);
        else if (action === 'open-invoice') {
          if (window.sectionManager) window.sectionManager.showSection('faktury');
          window.invoicesListManager?.openDetail(id);
        }
        return;
      }
      const row = e.target.closest('[data-row-id]');
      if (row && !e.target.closest('button, a, select')) {
        this.openDetail(row.dataset.rowId);
      }
    });
  }

  // ---------- Export ----------

  renderExportDropdown() {
    return `
      <div class="relative" id="orders-export-wrap">
        <button id="orders-export-btn" type="button" title="Exportovat aktuální výběr objednávek"
          class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500">
          ${icon('arrow-down-tray', 'w-4 h-4 mr-2')}
          Export
          ${icon('chevron-down', 'w-4 h-4 ml-2')}
        </button>
        <div id="orders-export-menu"
          class="hidden absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-20 py-1">
          <button type="button" data-export="csv"
            class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
            CSV (Excel)
          </button>
          <button type="button" data-export="xlsx"
            class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
            Excel (.xlsx)
          </button>
        </div>
      </div>
    `;
  }

  _bindExportEvents() {
    const exportBtn = document.getElementById('orders-export-btn');
    const exportMenu = document.getElementById('orders-export-menu');
    if (!exportBtn || !exportMenu) return;
    exportBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      exportMenu.classList.toggle('hidden');
    });
    exportMenu.querySelectorAll('[data-export]').forEach((item) => {
      item.addEventListener('click', () => {
        exportMenu.classList.add('hidden');
        this.triggerExport(item.dataset.export);
      });
    });
    // Zavření menu při kliku mimo (jen jednou)
    if (!this._exportOutsideClickBound) {
      this._exportOutsideClickBound = true;
      document.addEventListener('click', (e) => {
        const wrap = document.getElementById('orders-export-wrap');
        const menu = document.getElementById('orders-export-menu');
        if (!wrap || !menu) return;
        if (!wrap.contains(e.target)) menu.classList.add('hidden');
      });
    }
  }

  triggerExport(format) {
    // Export respektuje aktuální filtry seznamu (bez stránkování);
    // dateFrom/dateTo se u export endpointu jmenují from/to.
    const params = this.view.buildParams({ paginate: false });
    if (params.get('dateFrom')) { params.set('from', params.get('dateFrom')); params.delete('dateFrom'); }
    if (params.get('dateTo')) { params.set('to', params.get('dateTo')); params.delete('dateTo'); }
    params.set('format', format);
    const url = `${API_BASE_URL}/orders/export?${params.toString()}`;
    this.downloadExport(url, format);
    this.notify('Export spuštěn', 'info');
  }

  async downloadExport(url, format) {
    try {
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const body = await res.json();
          if (body?.error) msg = body.error;
        } catch (_) { /* není JSON */ }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `objednavky-${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      console.error('Chyba při exportu objednávek:', err);
      this.notify(err.message || 'Export se nezdařil', 'error');
    }
  }

  async downloadPdf(id) {
    try {
      const res = await fetch(`${API_BASE_URL}/orders/${id}/pdf`, { credentials: 'include' });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const body = await res.json();
          if (body?.error) msg = body.error;
        } catch (_) { /* není JSON */ }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `objednavka-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      this.notify('PDF objednávky staženo', 'success');
    } catch (err) {
      console.error('Chyba při stahování PDF objednávky:', err);
      this.notify(err.message || 'PDF se nepodařilo stáhnout', 'error');
    }
  }

  // ---------- API ----------

  /** Kompatibilita: vyroba.js volá fetchOrders() pro synchronizaci seznamu. */
  async fetchOrders() {
    return this.refresh();
  }

  async getOrder(id) {
    const res = await fetch(`${API_BASE_URL}/orders/${id}`, { credentials: 'include' });
    if (!res.ok) throw new Error('Nepodařilo se načíst objednávku');
    const json = await res.json();
    return json.data;
  }

  async setStatus(id, status, note = '') {
    const res = await fetch(`${API_BASE_URL}/orders/${id}/status`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, note })
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Nepodařilo se změnit stav objednávky');
    return json.data;
  }

  /** Uloží ruční pořadí karty v kanbanu (updateOrder propouští kanbanOrder přes PUT). */
  async saveKanbanOrder(id, orderKey) {
    const res = await fetch(`${API_BASE_URL}/orders/${id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kanbanOrder: orderKey })
    });
    if (!res.ok) {
      const json = await res.json().catch(() => ({}));
      throw new Error(json.error || 'Uložení pořadí se nezdařilo');
    }
  }

  async deleteOrder(id) {
    const res = await fetch(`${API_BASE_URL}/orders/${id}`, { method: 'DELETE', credentials: 'include' });
    if (!res.ok) {
      const json = await res.json().catch(() => ({}));
      throw new Error(json.error || 'Nepodařilo se smazat objednávku');
    }
    return true;
  }

  // ---------- Detail ----------

  async openDetail(id) {
    const sm = window.sectionManager;
    if (sm && sm.currentSection !== 'objednavky') {
      sm.switchToSection('objednavky', { skipLoad: true });
    }
    const container = document.getElementById('objednavky-section-content');
    if (!container) return;
    container.innerHTML = `
      <div class="flex items-center justify-center py-16 text-gray-400">
        ${icon('spinner', 'w-8 h-8 animate-spin')}
      </div>
    `;

    try {
      const order = await this.getOrder(id);
      this.renderDetail(order);
    } catch (err) {
      this.renderDetailError(err.message);
    }
  }

  renderDetailError(message) {
    const container = document.getElementById('objednavky-section-content');
    if (!container) return;
    container.innerHTML = `
      <div class="space-y-4">
        <button id="orders-back-btn"
          class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
          ${icon('chevron-left', 'w-4 h-4 mr-2')}
          Zpět na seznam
        </button>
        <div class="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-700">${escapeHtml(message)}</div>
      </div>
    `;
    document.getElementById('orders-back-btn')?.addEventListener('click', () => this.load());
  }

  renderDetail(o) {
    const container = document.getElementById('objednavky-section-content');
    if (!container) return;
    const created = new Date(o.createdAt).toLocaleString('cs-CZ');
    const updated = new Date(o.updatedAt).toLocaleString('cs-CZ');

    const totalPrice = o.totalPrice || 0;
    const totalWithVat = totalPrice * 1.21;

    container.innerHTML = `
      <div class="space-y-4">
        <button id="orders-back-btn"
          class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
          ${icon('chevron-left', 'w-4 h-4 mr-2')}
          Zpět na seznam
        </button>

        <div class="bg-white rounded-lg shadow border border-gray-200 p-6">
          <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
            <div>
              <h3 class="text-xl font-semibold text-gray-900">Objednávka #${escapeHtml(o.id)}</h3>
              <p class="text-sm text-gray-500 mt-1">
                Vytvořeno ${created} · vystavil ${escapeHtml(o.createdBy || '—')}
                ${o.offerId ? ` · z nabídky <button class="text-blue-600 hover:underline" id="orders-open-offer">#${escapeHtml(o.offerId)}</button>` : ''}
              </p>
            </div>
            <div class="flex items-center gap-3 flex-wrap">
              ${statusBadge('order', o.status, 'text-sm px-3 py-1')}
              <select id="orders-detail-status"
                class="px-3 py-2 border border-gray-300 rounded-md text-sm">
                ${this.view.statuses.map((s) => `<option value="${s}" ${s === o.status ? 'selected' : ''}>${escapeHtml(statusLabel('order', s) || s)}</option>`).join('')}
              </select>
              <button id="orders-detail-pdf" type="button"
                class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                ${icon('document-arrow-down', 'w-4 h-4 mr-1.5 text-gray-500')}
                PDF
              </button>
              <button id="orders-detail-print" type="button"
                class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                ${icon('printer', 'w-4 h-4 mr-1.5 text-gray-500')}
                Tisk
              </button>
              ${this.renderInvoiceAction(o)}
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-2">
            <div>
              <h4 class="text-sm font-semibold text-gray-700 mb-2">Poznámka k objednávce</h4>
              <textarea id="orders-detail-note" rows="4"
                class="w-full p-3 border border-gray-300 rounded-md text-sm resize-vertical focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Interní poznámka k zakázce (termín, doprava, speciální požadavky…). Uloží se automaticky a vytiskne se na objednávce.">${escapeHtml(o.note || '')}</textarea>
              <p id="orders-note-hint" class="text-xs text-gray-400 mt-1">Změny se ukládají automaticky po opuštění pole.</p>
            </div>
            <div>
              <h4 class="text-sm font-semibold text-gray-700 mb-2">Celková cena</h4>
              <div class="bg-gray-50 rounded-md p-3 border border-gray-100">
                <div class="flex justify-between text-sm"><span>Bez DPH</span><strong>${totalPrice.toLocaleString('cs-CZ')} Kč</strong></div>
                <div class="flex justify-between text-sm mt-1"><span>S DPH 21 %</span><strong>${totalWithVat.toLocaleString('cs-CZ', { maximumFractionDigits: 2 })} Kč</strong></div>
                <div class="flex justify-between text-xs text-gray-500 mt-2"><span>Položek</span><span>${(o.items || []).length}</span></div>
                <div class="flex justify-between text-xs text-gray-500"><span>Poslední změna</span><span>${updated}</span></div>
              </div>
            </div>
          </div>
        </div>

        <!-- A4 náhled objednávky (stejný vzor jako editor nabídky) -->
        <div class="bg-white rounded-lg shadow border border-gray-200 p-6 overflow-x-auto" id="orders-a4-preview">
          ${this.renderOrderPreview(o)}
        </div>

        <div class="bg-white rounded-lg shadow border border-gray-200 p-6">
          <h4 class="text-sm font-semibold text-gray-700 mb-3">Historie stavů</h4>
          ${this.renderStatusHistory(o.statusHistory)}
        </div>
      </div>
    `;

    document.getElementById('orders-back-btn')?.addEventListener('click', () => this.load());
    document.getElementById('orders-detail-pdf')?.addEventListener('click', () => this.downloadPdf(o.id));
    document.getElementById('orders-detail-print')?.addEventListener('click', () => this.printOrder(o));
    document.getElementById('orders-detail-note')?.addEventListener('change', async (e) => {
      try {
        const res = await fetch(`${API_BASE_URL}/orders/${o.id}`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: e.target.value })
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || 'Poznámku se nepodařilo uložit');
        o.note = json.data.note;
        const hint = document.getElementById('orders-note-hint');
        if (hint) hint.textContent = `Uloženo ${new Date().toLocaleTimeString('cs-CZ')}`;
      } catch (err) {
        this.notify(err.message, 'error');
      }
    });
    document.getElementById('orders-detail-status')?.addEventListener('change', async (e) => {
      try {
        const updated = await this.setStatus(o.id, e.target.value);
        this.notify(`Stav změněn na „${statusLabel('order', updated.status) || updated.status}"`, 'success');
        this.renderDetail(updated);
      } catch (err) {
        this.notify(err.message, 'error');
      }
    });
    document.getElementById('orders-open-offer')?.addEventListener('click', async () => {
      if (!window.offersListManager || !o.offerId) return;
      window.offersListManager.openOfferEditor(o.offerId);
    });
    document.getElementById('orders-issue-invoice-btn')?.addEventListener('click', () => this.handleIssueInvoice(o));
    document.getElementById('orders-open-invoice-btn')?.addEventListener('click', () => {
      if (!window.invoicesListManager || !o.invoicedAsId) return;
      if (window.sectionManager) window.sectionManager.showSection('faktury');
      window.invoicesListManager.openDetail(o.invoicedAsId);
    });
  }

  renderInvoiceAction(o) {
    if (o.invoicedAsId) {
      return `
        <button id="orders-open-invoice-btn"
          class="inline-flex items-center px-3 py-2 border border-indigo-300 text-sm font-medium rounded-md text-indigo-700 bg-white hover:bg-indigo-50">
          ${icon('receipt', 'w-4 h-4 mr-1.5')}
          Faktura ${escapeHtml(o.invoicedAsId)}
        </button>
      `;
    }
    if (o.status === 'cancelled') return '';
    const isReady = ['to_invoice', 'done', 'delivered', 'in_progress'].includes(o.status);
    if (!isReady) return '';
    return `
      <button id="orders-issue-invoice-btn"
        class="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500">
        ${icon('receipt', 'w-4 h-4 mr-1.5')}
        Vystavit fakturu
      </button>
    `;
  }

  async handleIssueInvoice(order) {
    if (!confirm(`Vystavit fakturu z objednávky #${order.id}?\nObjednávka přejde do stavu „Fakturováno".`)) return;
    try {
      const res = await fetch(`${API_BASE_URL}/invoices/from-order/${order.id}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Nepodařilo se vystavit fakturu');
      this.notify(`Vystavena faktura ${json.data.id}`, 'success');
      // Otevřít detail nově vystavené faktury v sekci Faktury
      if (window.sectionManager) window.sectionManager.showSection('faktury');
      if (window.invoicesListManager) {
        await window.invoicesListManager.openDetail(json.data.id);
      }
    } catch (err) {
      this.notify(err.message, 'error');
    }
  }

  renderStatusHistory(history) {
    if (!Array.isArray(history) || history.length === 0) {
      return `<p class="text-sm text-gray-500">Zatím bez změn stavu.</p>`;
    }
    return `
      <ol class="relative border-l border-gray-200 ml-3">
        ${history.slice().reverse().map((h) => {
          const m = STATUS_META.order[h.status] || { label: h.status, dot: 'bg-gray-400' };
          const time = new Date(h.changedAt).toLocaleString('cs-CZ');
          return `
            <li class="ml-4 pb-3">
              <span class="absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full border border-white ${m.dot}"></span>
              <div class="text-sm font-medium text-gray-900">${escapeHtml(m.label)}</div>
              <div class="text-xs text-gray-500">${time} · ${escapeHtml(h.changedBy || '—')}</div>
              ${h.note ? `<div class="text-xs text-gray-600 mt-0.5">${escapeHtml(h.note)}</div>` : ''}
            </li>
          `;
        }).join('')}
      </ol>
    `;
  }

  // ---------- A4 náhled a tisk (vzor: offer-editor.js) ----------

  /** A4 náhled objednávky – hlavička s logem, odběratel/dodavatel, položky se specifikací, součty. */
  renderOrderPreview(o) {
    const date = new Date(o.createdAt);
    const formattedDate = date.toLocaleDateString('cs-CZ', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
    const totalPrice = o.totalPrice || 0;
    const vatRate = 0.21;
    const totalPriceWithVat = totalPrice * (1 + vatRate);

    return `
      <div class="container a4-wrapper" style="max-width: 800px; margin: 0 auto; font-family: 'Plus Jakarta Sans Variable', 'Inter Tight', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
        <header style="display: flex; flex-direction: row; align-items: flex-start; padding-bottom: 22px; border-bottom: 1px solid black; margin-bottom: 20px;">
          <div style="width: 50%;">
            <img src="${logoUrl}" alt="Tiskárna Tria, s.r.o." width="140" height="45">
          </div>
          <div style="width: 50%; text-align: right;">
            <h1 style="font-size: 1.3em; margin: 0 0 10px 0; font-weight: 600;">Objednávka č. ${escapeHtml(o.id)}</h1>
            <div style="font-size: 0.9em; color: #5A6079;">Ze dne: ${formattedDate}</div>
            <div style="font-size: 0.9em; color: #5A6079;">Vystavil: ${escapeHtml(o.createdByName || o.createdBy || 'Systém')}</div>
            ${o.offerId ? `<div style="font-size: 0.9em; color: #5A6079;">Na základě nabídky č. ${escapeHtml(o.offerId)}</div>` : ''}
          </div>
        </header>

        <section style="display: flex; padding-top: 10px; flex-direction: row; align-items: flex-start; margin-bottom: 20px;">
          <div style="width: 50%;">
            <p style="font-size: 0.85em; letter-spacing: 0.04em; text-transform: uppercase; color: #5A6079; margin: 0 0 8px 0;">Odběratel</p>
            <pre style="font-family: inherit; font-size: 0.95em; margin: 0; white-space: pre-wrap; line-height: 1.5;">${escapeHtml(customerDataText(o.customerData) || '— bez zákazníka —')}</pre>
          </div>
          <div style="width: 50%; padding-left: 20px;">
            <p style="font-size: 0.85em; letter-spacing: 0.04em; text-transform: uppercase; color: #5A6079; margin: 0 0 8px 0;">Dodavatel</p>
            <div style="font-size: 0.95em; line-height: 1.5;">
              <p style="margin: 0 0 8px 0;">Tiskárna Tria s.r.o.<br>Olbramovice Ves 31,<br>259 01 Olbramovice</p>
              <p style="margin: 0 0 8px 0;">IČO: 49546112<br>DIČ: CZ49546112</p>
              <p style="margin: 0;">Email: tiskarna@tria.cz<br>Web: www.tria.cz</p>
            </div>
          </div>
        </section>

        <table style="width: 100%; border-collapse: collapse; margin: 10px 0 20px 0;">
          <thead>
            <tr style="border-bottom: 1px solid black;">
              <th style="padding: 8px 4px; text-align: left; font-size: 0.85em; letter-spacing: 0.04em; text-transform: uppercase; color: #5A6079;">Označení položky</th>
              <th style="padding: 8px 4px; text-align: right; font-size: 0.85em; letter-spacing: 0.04em; text-transform: uppercase; color: #5A6079; white-space: nowrap;">Počet kusů</th>
              <th style="padding: 8px 4px; text-align: right; font-size: 0.85em; letter-spacing: 0.04em; text-transform: uppercase; color: #5A6079; white-space: nowrap;">Cena za kus</th>
              <th style="padding: 8px 4px; text-align: right; font-size: 0.85em; letter-spacing: 0.04em; text-transform: uppercase; color: #5A6079;">Cena</th>
              <th style="padding: 8px 4px; text-align: right; font-size: 0.85em; letter-spacing: 0.04em; text-transform: uppercase; color: #5A6079; white-space: nowrap;">Cena s DPH</th>
            </tr>
          </thead>
          <tbody>
            ${(o.items || []).map((item) => this.renderOrderPreviewItemRow(item)).join('')}
          </tbody>
        </table>

        <section style="display: flex; justify-content: flex-end; margin-bottom: 16px;">
          <div style="min-width: 280px; font-size: 1em;">
            <div style="display: flex; justify-content: space-between; padding: 4px 0;">
              <span style="color: #5A6079;">Cena celkem (bez DPH)</span>
              <span>${totalPrice.toLocaleString('cs-CZ')} Kč</span>
            </div>
            <div style="display: flex; justify-content: space-between; padding: 4px 0;">
              <span style="color: #5A6079;">DPH 21 %</span>
              <span>${(totalPrice * vatRate).toLocaleString('cs-CZ', { maximumFractionDigits: 2 })} Kč</span>
            </div>
            <div style="display: flex; justify-content: space-between; padding: 8px 0 4px; margin-top: 4px; border-top: 1px solid black; font-weight: 700; font-size: 1.15em;">
              <span>Celkem s DPH</span>
              <span>${totalPriceWithVat.toLocaleString('cs-CZ', { maximumFractionDigits: 2 })} Kč</span>
            </div>
          </div>
        </section>

        ${o.note ? `
          <section style="margin-bottom: 16px; border: 1px solid #EDEEF3; border-radius: 6px; padding: 12px 14px; background: #FAFAFC;">
            <p style="font-size: 0.85em; letter-spacing: 0.04em; text-transform: uppercase; color: #5A6079; margin: 0 0 6px 0;">Poznámka</p>
            <p style="margin: 0; font-size: 0.95em; white-space: pre-wrap;">${escapeHtml(o.note)}</p>
          </section>
        ` : ''}
      </div>
    `;
  }

  renderOrderPreviewItemRow(item) {
    const vatRate = 0.21;
    const price = item.price || 0;
    const priceWithVat = price * (1 + vatRate);
    const quantity = Number(item.formData?.pocetKusu) || 1;
    const unitPrice = item.pricePerPiece || (quantity > 0 ? price / quantity : 0);
    const displayName = stripFinishingFromName(item.name || '');
    const finishingLines = item.formData ? buildFinishingLines(item.formData) : [];

    return `
      <tr style="${finishingLines.length ? '' : 'border-bottom: 1px solid #EDEEF3;'}">
        <td style="padding: 8px 4px; vertical-align: top;">
          <div style="font-weight: 500;">${escapeHtml(displayName)}</div>
          ${item.notes && item.notes.trim() ? `<div style="font-size: 0.85em; color: #5A6079; margin-top: 2px;">${escapeHtml(item.notes)}</div>` : ''}
        </td>
        <td style="padding: 8px 4px; text-align: right; white-space: nowrap; vertical-align: top;">${quantity.toLocaleString('cs-CZ')} ks</td>
        <td style="padding: 8px 4px; text-align: right; white-space: nowrap; vertical-align: top;">${unitPrice.toLocaleString('cs-CZ', { maximumFractionDigits: 2, minimumFractionDigits: 2 })} Kč</td>
        <td style="padding: 8px 4px; text-align: right; white-space: nowrap; vertical-align: top;">${price.toLocaleString('cs-CZ', { maximumFractionDigits: 2 })} Kč</td>
        <td style="padding: 8px 4px; text-align: right; white-space: nowrap; vertical-align: top;">${priceWithVat.toLocaleString('cs-CZ', { maximumFractionDigits: 2 })} Kč</td>
      </tr>
      ${finishingLines.length ? `
        <tr style="border-bottom: 1px solid #EDEEF3;">
          <td colspan="5" style="padding: 0 4px 10px 4px; font-size: 0.85em; color: #5A6079; line-height: 1.5;">
            ${finishingLines.map((l) => escapeHtml(l)).join('<br>')}
          </td>
        </tr>
      ` : ''}
    `;
  }

  /** Tisk objednávky – otevře nové okno s A4 náhledem a spustí tisk. */
  printOrder(o) {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      this.notify('Prohlížeč zablokoval otevření tiskového okna', 'error');
      return;
    }
    const absoluteLogoUrl = new URL(logoUrl, window.location.href).href;
    const content = this.renderOrderPreview(o).replace(logoUrl, absoluteLogoUrl);
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Objednávka ${escapeHtml(o.id)}</title>
        <meta charset="utf-8" />
        <style>
          body { margin: 0; padding: 20px; color: #0B0E2D; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
          .a4-wrapper { width: 190mm; }
          @media print {
            body { padding: 0; }
            .a4-wrapper { width: auto; }
          }
        </style>
      </head>
      <body>
        ${content}
        <script>
          window.onload = function() { setTimeout(function() { window.print(); }, 300); }
        <\/script>
      </body>
      </html>
    `);
    printWindow.document.close();
  }

  // ---------- Akce ----------

  async confirmDelete(id) {
    if (!confirm(`Opravdu smazat objednávku #${id}?`)) return;
    try {
      await this.deleteOrder(id);
      this.notify('Objednávka byla smazána', 'success');
      this.load();
    } catch (err) {
      this.notify(err.message, 'error');
    }
  }

  // ---------- Helpers ----------

  notify(msg, type = 'success') {
    if (typeof window.showNotification === 'function') window.showNotification(msg, type);
  }

  escape(str) {
    return escapeHtml(str);
  }
}

OrdersListManager.STATUS_META = STATUS_META.order;
OrdersListManager.STATUS_ORDER = ORDER_STATUS_ORDER;

window.OrdersListManager = OrdersListManager;
